import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { defer, queryStringToObject } from '/imports/utils';
import { getUser, listActiveUsers, userDisplayName } from '/imports/api/users';
import { FormField } from '/imports/ui/FormField';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { RichSelect } from '/imports/ui/RichSelect';
import { Checkbox } from '/imports/ui/Checkbox';
import { AccountCredentials } from '/imports/ui/AccountCredentials';
import { Modal } from '/imports/ui/Modal';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { NotFoundPage } from '/imports/ui/NotFoundPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useMethod, useUser } from '/imports/hooks';
import { autoForm, formConfig } from '/imports/ui/AutoForm';
import {
  canAdminAccounts, createAccount, Account,
  updateAccount, accountDisplayName, getAccount, deleteAccount, getAccountUser
} from '/imports/api/accounts';

export const AccountPage = () => {
  const currentUser = useUser();
  const { _id } = useParams<any>();
  const isNew = _id === 'new';
  const query = queryStringToObject();
  const queryUserId = query?.userId;
  const [queryUser, loadingQueryUser] = queryUserId ? useMethod(getUser, queryUserId, [queryUserId]) : [undefined, false];
  const [update, setUpdate] = useState<Partial<Account>>(isNew ? { _id, active: true, orgId: currentUser?.orgId, userId: queryUserId } : { _id });
  useEffect(() => {
    if (!queryUser) return;
    toast.info('Configure the user\'s Account in the current Org');
    const { firstName, lastName, email, phone } = queryUser;
    queryUser && setUpdate({ ...update, userId: queryUser._id, firstName, lastName, email, phone });
  }, [queryUser?._id]);
  const [showAccountCredentials, setShowAccountCredentials] = useState(false);
  const [document, loadingDocument] = isNew ? [undefined, false] : useMethod(getAccount, _id as string);
  const [user, loadingUser] = isNew ? [undefined, false] : useMethod(getAccountUser, _id as string);
  const [initializing, setInitializing] = useState(true);
  const initialized = currentUser && !loadingDocument && !loadingUser;
  const form = formConfig<Account>([
    [{ field: 'active', input: { type: Checkbox } }],
    [
      currentUser?.isSuperUser ? {
        field: 'userId', label: strings.User(), input: (setUpdate, update, _, error) => (
          <FormField key="userId" field="userId" error={error}>
            <label>{strings.User()}</label>
            <RichSelect
              id="acct-user"
              listMethod={listActiveUsers}
              value={update.userId || document?.userId || queryUserId}
              preSelectedOption={user ? { value: user._id, label: `${userDisplayName(user)} (${user.username})` } : undefined}
              onChange={(option) => {
                const _update: Partial<Account> = { userId: option.value };
                if (!update.firstName) _update.firstName = option.data.firstName;
                if (!update.lastName) _update.lastName = option.data.lastName;
                if (!update.email) _update.email = option.data.email;
                if (!update.phone) _update.phone = option.data.phone;
                setUpdate({ ...update, ..._update });
              }}
            />
          </FormField>
        )
      } : null,
      { field: 'firstName' }, { field: 'lastName' }
    ],
    [{ field: 'email' }, { field: 'phone', input: { type: PhoneInput } }],
    [{
      field: 'roles', input: (setUpdate, update, document, error) => (
        <div key="roles-div" className="roles flex flex-col [&_h2]:mb-2">
          <h2>{strings.Roles()}</h2>
          <FormField key="roles" className="checkbox roles-checkboxes flex gap-10" field="roles" error={error}>
            <RoleCheckbox role="admin" label={strings.Admin()} document={document} update={update} setUpdate={setUpdate} />
            <RoleCheckbox role="dispatcher" label={strings.Dispatcher()} document={document} update={update} setUpdate={setUpdate} />
          </FormField>
        </div>
      )
    }],
  ], [user]);
  const [AccountForm] = autoForm({
    document,
    createMethod: createAccount as any,
    updateMethod: updateAccount,
    deleteMethod: deleteAccount,
    form,
    update,
    setUpdate,
    // defaultUpdate: isNew ? { _id, active: true, orgId: currentUser?.orgId, userId: queryUserId } : { _id },
    redirect: '/accounts'
  });
  useEffect(() => {
    initialized && form && defer(() => setInitializing(false));
  }, [form, initialized]);
  if (initializing || loadingQueryUser) return <LoadingFlex />;
  if (!canAdminAccounts(currentUser, document?.orgId)) return <NotPermittedPage />;
  if (!isNew && !document) return <NotFoundPage />;
  return (
    <Page className={`page [&_.credentials]:mt-12 [&_.credentials-modal_.modal-body]:pt-8! [&_.credentials-modal_.modal-body_form]:pb-0!${!initialized ? ' initializing' : ''}`}
      breadcrumbs={[{ path: '/accounts', label: strings.Accounts() }, { path: `/accounts/${_id}`, label: document && accountDisplayName(document) || strings.New() }]}
    >
      <PageHeading
        left={
          <h1>
            {isNew ? strings.NewAccount() : document && accountDisplayName(document)}
          </h1>
        }
      />
      <PageBody>
        {AccountForm}
      </PageBody>
      {
        showAccountCredentials ?
          (
            <Modal
              className="credentials-modal"
              title={strings.SetUsernameOrPassword()}
              onClose={() => setShowAccountCredentials(false)}
            >
              <AccountCredentials accountId={_id || 'new'} onSuccess={() => {
                toast.success(strings.PasswordSuccessfullyChanged());
                setShowAccountCredentials(false);
              }} />
            </Modal>
          )
          :
          null
      }
    </Page>
  );
};

interface RoleCheckboxProps {
  role: string;
  document?: Account;
  update: Partial<Account>;
  setUpdate: ((value: React.SetStateAction<Partial<Account>>) => void) | undefined;
  label: string;
}

const RoleCheckbox = (props: RoleCheckboxProps) => {
  const { role, document, update, setUpdate, label } = props;
  return (
    <label>
      <Checkbox
        checked={!!(update.roles?.includes(role) ?? document?.roles?.includes(role))}
        onChange={(checked) => {
          let roles;
          if (checked) roles = [...(update.roles || document?.roles || []), role];
          else roles = (update.roles || document?.roles || []).filter((r) => r !== role);
          setUpdate?.({ ...update, roles });
        }}
      />
      {label}
    </label>
  );
};